import type { ClientExportListRequest } from '@ghp/api-client'
import type {
  ClientExportDelivery,
  ClientExportGrouping,
  ClientExportSelectionFilter,
} from '@ghp/domain'
import { z } from 'zod'

import { urlCount, urlText } from '../../../lib/search-params'

/**
 * Le modèle de l'écran « Export clients » : le schéma des `search params`, les trois onglets, et
 * la **couture** vers la requête serveur. Hors de la route pour que le composant d'écran reste du
 * markup.
 *
 * ⚠️ Même discipline que les écrans « Demandes » et « Contrôles » : l'URL **est** l'état, chaque
 * champ porte un `.catch` (M21 appliqué à l'URL), et **rien n'est filtré en mémoire** (D24).
 */

/**
 * Les trois vues de la file. Chacune est un **préréglage du filtre serveur** `delivery`, pas un
 * concept de plus (M14 § 1).
 *
 * ⚠️ **« À exporter » est la vue par défaut**, et c'est le sujet de l'écran : ce qui reste dû au
 * client. Ouvrir sur « Tous » ferait relire chaque fois la liste des livraisons passées pour y
 * retrouver le travail du jour.
 */
export const DELIVERIES = [
  { value: 'pending', label: 'À exporter' },
  { value: 'delivered', label: 'Déjà exportés' },
  { value: 'all', label: 'Tous' },
] as const satisfies readonly { value: ClientExportDelivery; label: string }[]

/**
 * Le « Trier » du prototype (`exportClients`), mot pour mot et dans son ordre. Chaque valeur est
 * un **ordre serveur** (`clientExportGroupingSchema`), pas une mise en forme de la page.
 */
export const GROUPINGS = [
  { value: 'lot', label: 'Secteur' },
  { value: 'client', label: 'Client' },
  { value: 'city', label: 'Ville' },
  { value: 'period', label: 'Période' },
] as const satisfies readonly { value: ClientExportGrouping; label: string }[]

const GROUPING_VALUES = GROUPINGS.map(({ value }) => value) as [
  ClientExportGrouping,
  ...ClientExportGrouping[],
]

const DELIVERY_VALUES = DELIVERIES.map(({ value }) => value) as [
  ClientExportDelivery,
  ...ClientExportDelivery[],
]

/**
 * Les onglets du prototype (`data-exp-tab`) : « Par lot », la file entière rangée par le serveur ;
 * « Ventes », les contrôles de vente (`purpose = 'sale'`) — à plat, une vente n'a pas de lot ; et
 * « Saisie », les rapports dont la saisie reste à vérifier avant l'export définitif
 * (`entry = 'pending'`).
 */
export const EXPORT_VIEWS = [
  { value: 'lot', label: 'Par secteur' },
  { value: 'sale', label: 'Ventes' },
  { value: 'entry', label: 'Saisie' },
] as const

export type ClientExportView = (typeof EXPORT_VIEWS)[number]['value']

export const clientExportSearchSchema = z.object({
  view: z.enum(['lot', 'sale', 'entry']).default('lot').catch('lot'),
  /** La recherche de l'en-tête — « Réf, adresse, client… », comme au prototype. */
  q: urlText(''),
  delivery: z.enum(DELIVERY_VALUES).default('pending').catch('pending'),
  offset: urlCount(0),
  /** Le client — **son identifiant, pas son nom** (M22), et il part au serveur (#661). */
  clientId: urlText('all'),
  grouping: z.enum(GROUPING_VALUES).default('lot').catch('lot'),
})

export type ClientExportSearch = z.infer<typeof clientExportSearchSchema>

/** Les valeurs par défaut du schéma, partagées entre l'URL et « Réinitialiser ». */
export const CLIENT_EXPORT_DEFAULTS: ClientExportSearch = clientExportSearchSchema.parse({})

/**
 * Ce que le filtre non servi attend, nommé — pour que l'écran le dise à l'utilisateur au lieu de
 * le laisser deviner devant un contrôle inerte.
 *
 * ⚠️ Le prototype validé range les exports **par client, par lot et par période**. `Client` et
 * `Lot` existent désormais, mais rien ne relie encore un `ClientExport` à l'un ou à l'autre :
 * l'écran livre donc la file **par contrôle**, ce qui est la granularité de la trace de livraison
 * (M26). Le regroupement se posera **au-dessus** de cette file sans la refaire.
 *
 * ⚠️ **Le client n'est pas dans cette table** : le filtre part au serveur.
 * Le champ reste déclaré vide pour que le type garde son sens — ce qui n'est pas servi se nomme.
 */
export const UNSERVED_FILTERS = {} as const satisfies Partial<
  Record<keyof ClientExportSearch, string>
>

/** Combien de lignes une page demande — même densité que les autres tableaux du bureau. */
export const CLIENT_EXPORT_PAGE_SIZE = 50

/**
 * **La couture** : l'état d'URL de l'écran vers ce que l'API accepte (D21).
 *
 * C'est ici — et nulle part ailleurs — que vit l'écart entre ce que le prototype montre et ce que
 * le modèle porte. Un filtre absent de cette fonction est un filtre que le serveur n'applique pas.
 */
export function toClientExportQuery(search: ClientExportSearch): ClientExportListRequest {
  return {
    ...toClientExportSelection(search),
    limit: CLIENT_EXPORT_PAGE_SIZE,
    offset: search.offset,
  }
}

/**
 * Le filtre de « Tout exporter » : **le même** que la page, sans ses bornes — ce que l'écran montre
 * est ce qui part (M14 § 5).
 */
export function toClientExportSelection(search: ClientExportSearch): ClientExportSelectionFilter {
  return {
    // « Saisie » porte sa propre règle (non saisi **et** non livré) : le serveur l'applique seul.
    delivery: search.view === 'entry' ? 'all' : search.delivery,
    clientId: search.clientId,
    search: search.q.trim(),
    purpose: search.view === 'sale' ? 'sale' : 'all',
    entry: search.view === 'entry' ? 'pending' : 'all',
    // Sous « Ventes » et « Saisie », la liste est à plat, du plus récent au plus ancien — le tri
    // « Période » du serveur : une vente n'a pas de lot, et une saisie se traite une à une.
    grouping: search.view === 'lot' ? search.grouping : 'period',
  }
}
